import { useEffect, useId, useState, type ReactNode } from 'react';

/* --------------------------------------------------------------------------
 * Схемы упражнений площадки.
 *
 * Каждое упражнение — это расстановка конусов и траектория, по которой едет
 * мотоцикл. Словами «габаритный коридор» или «змейка» ничего не объясняют
 * тому, кто ни разу не был на площадке, а одна картинка объясняет сразу.
 *
 * Все схемы нарисованы сверху, в одной системе координат 240×140, чтобы
 * упражнения можно было сравнивать между собой. Единственное исключение —
 * подъём: уклон сверху не виден, поэтому он показан сбоку и отличается
 * штриховкой земли.
 * -------------------------------------------------------------------------- */

const VIEW_WIDTH = 240;
const VIEW_HEIGHT = 140;

/** Осевая линия площадки: относительно неё расставлено большинство схем. */
const AXIS = 70;

interface Exercise {
  title: string;
  /** Траектория мотоцикла. `null` — упражнение выполняется на месте. */
  path: string | null;
  /** Конусы, которые объезжают. */
  cones?: Array<[number, number]>;
  /** Дополнительная разметка: линии старта, зоны торможения, уклон. */
  marks?: ReactNode;
}

/** Два ряда конусов вдоль оси — коридор заданной ширины. */
function corridor(xs: number[], halfWidth: number): Array<[number, number]> {
  return xs.flatMap((x): Array<[number, number]> => [
    [x, AXIS - halfWidth],
    [x, AXIS + halfWidth],
  ]);
}

/** Поперечная линия: старт, финиш, момент подачи команды на торможение. */
function CrossLine({
  x,
  tone = 'muted',
  dashed = false,
}: {
  x: number;
  tone?: 'muted' | 'accent';
  dashed?: boolean;
}): React.JSX.Element {
  return (
    <line
      x1={x}
      y1={AXIS - 30}
      x2={x}
      y2={AXIS + 30}
      stroke={tone === 'accent' ? 'var(--color-accent)' : 'rgba(255,255,255,0.35)'}
      strokeWidth={tone === 'accent' ? 2.5 : 1.5}
      strokeDasharray={dashed ? '4 5' : undefined}
      strokeLinecap="round"
    />
  );
}

/** Стрелки вдоль движения: показывают, где разгоняются, а где уже тормозят. */
function Chevrons({
  from,
  to,
  y = AXIS,
  count = 4,
}: {
  from: number;
  to: number;
  y?: number;
  count?: number;
}): React.JSX.Element {
  const step = (to - from) / count;

  return (
    <g stroke="rgba(255,255,255,0.3)" strokeWidth="1.5" strokeLinecap="round" fill="none">
      {Array.from({ length: count }, (_, index) => {
        const x = from + step * index;
        return <path key={x} d={`M${x} ${y - 6} L${x + 7} ${y} L${x} ${y + 6}`} />;
      })}
    </g>
  );
}

/* Кортеж, а не массив: список заведомо непустой, и запасной элемент для
   несуществующего индекса берётся без проверки на `undefined`. */
const EXERCISES: [Exercise, ...Exercise[]] = [
  {
    title: 'Посадка и работа с органами управления',
    // Единственное упражнение без траектории: мотоцикл стоит на месте.
    path: null,
    // Мотоцикл сверху: колёса, руль и подсвеченные рычаги под руками
    marks: (
      <g>
        <rect
          x={86}
          y={AXIS - 42}
          width={68}
          height={84}
          rx={8}
          fill="none"
          stroke="rgba(255,255,255,0.14)"
          strokeWidth="1.5"
          strokeDasharray="5 6"
        />

        <g stroke="rgba(255,255,255,0.5)" strokeWidth="2" strokeLinecap="round" fill="none">
          <line x1={120} y1={AXIS - 26} x2={120} y2={AXIS + 26} />
          <rect x={117} y={AXIS - 32} width={6} height={16} rx={3} fill="rgba(255,255,255,0.5)" stroke="none" />
          <rect x={117} y={AXIS + 16} width={6} height={16} rx={3} fill="rgba(255,255,255,0.5)" stroke="none" />
          <line x1={100} y1={AXIS - 22} x2={140} y2={AXIS - 22} />
        </g>

        <g fill="var(--color-accent)">
          <circle cx={99} cy={AXIS - 22} r={4} />
          <circle cx={141} cy={AXIS - 22} r={4} />
        </g>
        <g fill="none" stroke="var(--color-accent)" strokeWidth="1.2" opacity="0.45">
          <circle cx={99} cy={AXIS - 22} r={9} />
          <circle cx={141} cy={AXIS - 22} r={9} />
        </g>
      </g>
    ),
  },
  {
    title: 'Трогание и остановка',
    path: `M34 ${AXIS} L206 ${AXIS}`,
    marks: (
      <g>
        <CrossLine x={34} />
        <CrossLine x={206} tone="accent" />
      </g>
    ),
  },
  {
    title: 'Движение на малой скорости',
    path: `M28 ${AXIS} L212 ${AXIS}`,
    // Широкий коридор: держать равновесие на скорости шага, не задевая границ.
    cones: corridor([75, 125, 175], 26),
  },
  {
    title: 'Змейка',
    cones: [
      [60, AXIS],
      [100, AXIS],
      [140, AXIS],
      [180, AXIS],
    ],
    path:
      `M24 ${AXIS} C 42 ${AXIS} 46 42 60 42 C 76 42 84 98 100 98 ` +
      `C 116 98 124 42 140 42 C 156 42 164 98 180 98 C 196 98 202 ${AXIS} 216 ${AXIS}`,
  },
  {
    title: 'Восьмёрка',
    cones: [
      [74, AXIS],
      [166, AXIS],
    ],
    path:
      `M120 ${AXIS} C 92 40 58 44 58 ${AXIS} C 58 98 92 100 120 ${AXIS} ` +
      `C 148 40 182 44 182 ${AXIS} C 182 98 148 100 120 ${AXIS} Z`,
  },
  {
    title: 'Габаритный коридор',
    path: `M28 ${AXIS} L212 ${AXIS}`,
    // Тот же проезд, что и на малой скорости, но коридор вдвое уже.
    cones: corridor([60, 95, 130, 165, 200], 13),
  },
  {
    title: 'Разгон и торможение',
    path: `M28 ${AXIS} L212 ${AXIS}`,
    marks: (
      <g>
        <Chevrons from={48} to={124} />
        {/* Зона торможения — размеченный участок, а не заливка: сплошной
            прямоугольник поверх асфальта даёт грязный оливковый цвет */}
        <rect
          x={150}
          y={AXIS - 24}
          width={62}
          height={48}
          rx={3}
          fill="none"
          stroke="var(--color-accent)"
          strokeWidth="1.2"
          strokeDasharray="3 5"
          opacity="0.5"
        />
        <CrossLine x={150} dashed />
      </g>
    ),
  },
  {
    title: 'Экстренное торможение',
    path: `M28 ${AXIS} L182 ${AXIS}`,
    marks: (
      <g>
        {/* Команда подаётся внезапно — между ней и остановкой очень мало места */}
        <CrossLine x={140} dashed />
        <CrossLine x={182} tone="accent" />
        <g stroke="rgba(255,255,255,0.22)" strokeWidth="3" strokeLinecap="round">
          <line x1={144} y1={AXIS - 5} x2={180} y2={AXIS - 5} />
          <line x1={144} y1={AXIS + 5} x2={180} y2={AXIS + 5} />
        </g>
      </g>
    ),
  },
  {
    title: 'Повороты и траектория',
    // Заходят широко, к вершине прижимаются, из поворота снова расходятся.
    path: 'M24 106 C 92 106 126 102 148 76 C 168 52 174 36 176 22',
    // Конус у вершины поворота: к нему прижимаются в самой узкой точке дуги
    cones: [[162, 92]],
  },
  {
    title: 'Старт на подъёме',
    path: 'M28 110 L108 110 L214 46',
    marks: (
      <g>
        {/* Вид сбоку: сверху уклон не виден. Штриховка — земля под колёсами */}
        <path
          d="M28 110 L108 110 L214 46"
          stroke="rgba(255,255,255,0.18)"
          strokeWidth="1.5"
          fill="none"
        />
        <g stroke="rgba(255,255,255,0.28)" strokeWidth="1.8" strokeLinecap="round">
          {[38, 58, 78, 98, 120, 144, 168, 192, 210].map((x) => {
            const groundY = x <= 108 ? 110 : 110 - (x - 108) * 0.6;
            return <line key={x} x1={x} y1={groundY} x2={x - 10} y2={groundY + 12} />;
          })}
        </g>

        {/*
          Останавливаются и удерживают мотоцикл на самом уклоне, а не у его
          подножия — в этом всё упражнение.
        */}
        <line
          x1={160}
          y1={68}
          x2={160}
          y2={90}
          stroke="var(--color-accent)"
          strokeWidth="2"
          strokeDasharray="4 4"
          strokeLinecap="round"
          opacity="0.7"
        />
      </g>
    ),
  },
];

export const EXERCISE_TITLES: string[] = EXERCISES.map((exercise) => exercise.title);

/** Конус: то же, что нарисовано на баннере школы. */
function Cone({ x, y }: { x: number; y: number }): React.JSX.Element {
  return (
    <g transform={`translate(${x} ${y})`}>
      <path d="M0 -8 L5.5 5 L-5.5 5 Z" fill="var(--color-accent)" opacity="0.85" />
      <path d="M-2.6 -2 L2.6 -2" stroke="var(--color-night)" strokeWidth="1.6" opacity="0.7" />
      <rect x={-7.5} y={5} width={15} height={2.5} rx={1.2} fill="var(--color-accent)" opacity="0.5" />
    </g>
  );
}

/**
 * Движение — украшение, и оно должно отключаться. `animateMotion` нельзя
 * погасить медиазапросом, поэтому предпочтение проверяется здесь и точка
 * просто не рисуется.
 */
function usePrefersReducedMotion(): boolean {
  const [prefers, setPrefers] = useState(false);

  useEffect(() => {
    const query = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefers(query.matches);

    const onChange = (event: MediaQueryListEvent): void => setPrefers(event.matches);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);

  return prefers;
}

/**
 * Схема одного упражнения. `key` на элементе с индексом упражнения заставляет
 * React пересоздать узел при переключении — иначе анимация отрисовки
 * траектории не запустится заново.
 */
export function ExerciseFigure({ index }: { index: number }): React.JSX.Element {
  const exercise = EXERCISES[index] ?? EXERCISES[0];
  const pathId = useId();
  const isStill = usePrefersReducedMotion();

  return (
    <svg
      viewBox={`0 0 ${VIEW_WIDTH} ${VIEW_HEIGHT}`}
      className="w-full"
      role="img"
      aria-label={`Схема упражнения «${exercise.title}»`}
    >
      {exercise.marks}

      {exercise.path && (
        <>
          {/*
            `pathLength` приводит длину любой кривой к единице — одна и та же
            анимация отрисовки подходит и прямой, и восьмёрке.
          */}
          <path
            id={pathId}
            d={exercise.path}
            pathLength={1}
            fill="none"
            stroke="var(--color-accent)"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="figure-path"
          />

          {!isStill && (
            <circle r="4.5" fill="#fff">
              <animateMotion dur="3.4s" repeatCount="indefinite" rotate="auto">
                <mpath href={`#${CSS.escape(pathId)}`} />
              </animateMotion>
            </circle>
          )}
        </>
      )}

      {exercise.cones?.map(([x, y]) => <Cone key={`${x}-${y}`} x={x} y={y} />)}
    </svg>
  );
}
